/**
 * attachmentPreview.js — open/download a project attachment WITH auth.
 *
 * window.open() is a raw navigation — it can't carry the Entra bearer token
 * (only the patched window.fetch does, src/index.js), and the API enforces
 * auth on GETs, so a plain window.open() 401s. Fetch the bytes with auth,
 * then hand the browser a same-origin blob URL.
 *
 * Shared by AttachmentsPanel (Doc Hub) and the Summary tab's invoice/PO/quote
 * deep links — one implementation so the two can never drift.
 */

// `base` is '/api', or the KSA tab's own prefix (useApiBase) for KSA files.
export async function fetchBlobUrl(attId, inline, base = '/api') {
  const r = await fetch(`${base}/attachments/file/${encodeURIComponent(attId)}${inline ? '?inline=1' : ''}`);
  if (!r.ok) return null;
  return URL.createObjectURL(await r.blob());
}

export async function previewAttachment(attId, base = '/api') {
  // Open the tab synchronously inside the click gesture so it isn't
  // popup-blocked, then point it at the blob once the fetch resolves.
  const w = window.open('', '_blank');
  const url = await fetchBlobUrl(attId, true, base);
  if (!url) {
    if (w) w.close();
    alert('Could not open the file.');
    return;
  }
  if (w) w.location = url;
  else window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function downloadAttachment(attId, filename, base = '/api') {
  const url = await fetchBlobUrl(attId, false, base);
  if (!url) {
    alert('Could not download the file.');
    return;
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Doc label chip colors — shared by Doc Hub cards and anywhere a label shows. */
export const DOC_TYPE_STYLES = {
  invoice: { label: 'INVOICE', color: '#92400E', bg: '#FEF3C7', border: '#FDE68A' },
  po: { label: 'PO', color: '#1565C0', bg: '#E3F2FD', border: '#BBDEFB' },
  quote: { label: 'QUOTE', color: '#1B5E20', bg: '#E8F5E9', border: '#C8E6C9' },
  // RFQ matches the 'rfq' Doc Hub folder in spelling on purpose - the two are
  // separate axes and reading one as the other is the easy mistake here.
  rfq: { label: 'RFQ', color: '#6A1B9A', bg: '#F3E5F5', border: '#E1BEE7' },
  general: { label: 'GENERAL', color: '#616161', bg: '#F5F5F5', border: '#E0E0E0' },
};

/* Invoice / PO / quote numbers are clickable on the tables, so those labels
   are useless without their number. An RFQ is filed, not linked - it may not
   have a number at all, so it is optional like General. */
export const REF_OPTIONAL = new Set(['general', 'rfq']);

/** Normalize a reference for matching: "PO# 12-345" ≈ "12345", "Q5296" ≈ "5296". */
export const normalizeDocRef = (v) =>
  String(v ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/^q(?=\d)/, '');

/**
 * Which of several files carrying label `type` + number `ref` a clicked number
 * opens, and why: { file, how } with how = 'chosen' (a person linked it) or
 * 'auto' (nobody did: a PDF, since a browser can preview it and not an .xlsm;
 * among those the newest). null when nothing matches.
 *
 * The SAME order as the server's GET /attachments/index, which feeds the table
 * links - so the Doc Hub badge, the detail panel and the table always agree.
 */
export function pickDocFor(attachments, type, ref) {
  const want = normalizeDocRef(ref);
  if (!want) return null;
  const matches = (attachments || []).filter((a) => a.doc_type === type && normalizeDocRef(a.doc_ref) === want);
  if (!matches.length) return null;
  const rank = (a) => [a.linked ? 0 : 1, a.mime === 'application/pdf' ? 0 : 1, -new Date(a.created_at || 0).getTime(), String(a.id)];
  const best = [...matches].sort((x, y) => {
    const a = rank(x);
    const b = rank(y);
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1;
    return 0;
  })[0];
  return { file: best, how: best.linked ? 'chosen' : 'auto' };
}

/** The attachment a number opens (or null) - see pickDocFor. */
export const findDocFor = (attachments, type, ref) => pickDocFor(attachments, type, ref)?.file || null;

/** The RFQ a person marked as THE RFQ for this record's quote, or null. An RFQ
 *  keeps its own number (the customer's, e.g. 93447R0), so this is by link. */
export const findLinkedRfq = (attachments) => (attachments || []).find((a) => a.doc_type === 'rfq' && a.linked) || null;
